import React from 'react';
import PropTypes from 'prop-types';
import { ApolloClient, ApolloProvider, HttpLink, InMemoryCache, gql, useQuery } from '@apollo/client';
import PolicyModal from '../../components/Succession/Legal/PolicyModal';

export const GET_PRIVACY_POLICY = gql`
  query GetPrivacyPolicy {
    getPrivacyPolicy
  }
`;

export const GET_TERMS_OF_SERVICE = gql`
  query GetTermsOfService {
    getTermsOfService
  }
`;

const QUERIES = {
  privacy: { query: GET_PRIVACY_POLICY, field: 'getPrivacyPolicy' },
  terms: { query: GET_TERMS_OF_SERVICE, field: 'getTermsOfService' },
};

// SidebarNavigation is mounted as its own ReactOnRails root with no ApolloProvider,
// so the container brings a client of its own. GraphqlController enforces CSRF
// outside development, so the token is read from the page on every request.
const csrfFetch = (uri, options = {}) => {
  const token = document.querySelector('meta[name="csrf-token"]')?.content;
  return fetch(uri, { ...options, headers: { ...options.headers, 'X-CSRF-Token': token } });
};

let client = null;
const getClient = () => {
  if (!client) {
    client = new ApolloClient({
      link: new HttpLink({ uri: '/graphql', credentials: 'same-origin', fetch: csrfFetch }),
      cache: new InMemoryCache(),
    });
  }
  return client;
};

const PolicyModalQuery = ({ policy, onClose }) => {
  const config = QUERIES[policy];
  const { data, loading, error } = useQuery(config.query, { fetchPolicy: 'cache-first' });
  return (
    <PolicyModal
      policy={policy}
      html={data ? data[config.field] : null}
      loading={loading}
      error={error}
      onClose={onClose}
    />
  );
};

PolicyModalQuery.propTypes = {
  policy: PropTypes.oneOf(['privacy', 'terms']).isRequired,
  onClose: PropTypes.func.isRequired,
};

/**
 * Controlled by the caller: `policy` is 'privacy', 'terms', or null (closed).
 */
const PolicyModalContainer = ({ policy, onClose }) => {
  if (!QUERIES[policy]) return null;
  return (
    <ApolloProvider client={getClient()}>
      <PolicyModalQuery policy={policy} onClose={onClose} />
    </ApolloProvider>
  );
};

PolicyModalContainer.propTypes = {
  policy: PropTypes.oneOf(['privacy', 'terms']),
  onClose: PropTypes.func.isRequired,
};

PolicyModalContainer.defaultProps = {
  policy: null,
};

export default PolicyModalContainer;
